import { Button, Dialog, Icon, Label, ScrollArea, Tooltip, token } from "@may-i/mash-design-system";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import type { AnalysisConfig } from "~/entities/analysis-config/types";
import type { Camera } from "~/entities/camera/types";
import { useRestoreBodyPointerEvents } from "~/shared/hooks/use-restore-body-pointer-events";
import { compareCamId } from "~/shared/lib/compare-cam-id";

export interface ValidationRow {
  cameraId: number;
  analysisNumber: string;
  type: "line" | "zone";
  name: string;
  key: string;
  result: "pass" | "fail" | null;
  reasons: ReasonEntry[];
}

function extractKeySuffix(key: string): string {
  const parts = key.split("_");
  return parts.length > 2 ? parts.slice(2).join("_") : parts.at(-1) ?? "";
}

type ReasonEntry = { key: string; values?: Record<string, string> };

function validateKey(suffix: string, whitelist: Set<string>): { ok: boolean; reasons: ReasonEntry[] } {
  const raw = suffix.trim();
  if (!raw) return { ok: false, reasons: [{ key: "validation.reason.empty" }] };

  const parts = raw.split("-").map((p) => p.trim()).filter((p) => p.length > 0);
  const reasons: ReasonEntry[] = [];

  if (parts.length < 2) {
    reasons.push({ key: "validation.reason.tooFewParts" });
    return { ok: false, reasons };
  }

  const first = parts[0].toLowerCase();
  const last = parts[parts.length - 1].toLowerCase();
  const middle = parts.slice(1, -1);

  if (!/^b\d+$/i.test(first) && !/^\d+f$/i.test(first)) {
    reasons.push({ key: "validation.reason.firstFloorFormat", values: { value: parts[0] } });
  }

  if (!/^re(?:00[1-9]|01[0-2]|260[1-9]|261[0-2])$/i.test(last)) {
    reasons.push({
      key: "validation.reason.lastFormat",
      values: { value: parts[parts.length - 1] },
    });
  }

  if (middle.length > 0) {
    for (let i = 0; i < middle.length - 1; i++) {
      const m = middle[i].toLowerCase();
      if (!whitelist.has(m)) {
        reasons.push({
          key: "validation.reason.middleNotInWhitelist",
          values: { value: middle[i] },
        });
      }
    }

    const lastMiddle = middle[middle.length - 1];
    const match = lastMiddle.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) {
      reasons.push({
        key: "validation.reason.lastMiddleFormat",
        values: { value: lastMiddle },
      });
    } else {
      const base = match[1].toLowerCase();
      if (!whitelist.has(base)) {
        reasons.push({
          key: "validation.reason.lastMiddlePrefixNotInWhitelist",
          values: { value: match[1] },
        });
      }
    }
  }

  return { ok: reasons.length === 0, reasons };
}

type SortOrder = "asc" | "desc";

interface KeyValidationDialogProps {
  open: boolean;
  onClose: () => void;
  localConfigs: AnalysisConfig[];
  cameras: Camera[];
  getDisplayName: (camera: Camera) => string;
  clientUid?: string;
}

export function KeyValidationDialog({
  open,
  onClose,
  localConfigs,
  cameras,
  getDisplayName,
  clientUid,
}: KeyValidationDialogProps) {
  const { t } = useTranslation("analysis-draft");
  const { branchUid } = useParams();
  const navigate = useNavigate();
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<ValidationRow[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  // 4-6-1 근본 해결: navigate 를 Radix 의 close cleanup 완료 시점(`onCloseAutoFocus`) 후로 미룬다.
  // 그 시점은 Dialog content unmount + body 의 inert/aria-hidden/scroll-lock 정리 완료 후라
  // 이전의 body 직접 cleanup workaround(0/50/200ms 타이머) 불필요.
  const pendingNavRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const newRows: ValidationRow[] = [];

    for (const cfg of localConfigs) {
      const camera = cameras.find((c) => c.id === cfg.cameraId);
      if (!camera) continue;
      const displayName = getDisplayName(camera);

      for (const line of cfg.countingLines) {
        newRows.push({
          cameraId: cfg.cameraId,
          analysisNumber: displayName,
          type: "line",
          name: line.analysisConfig?.name ?? "",
          key: line.key,
          result: null,
          reasons: [],
        });
      }
      for (const zone of cfg.intervalZones) {
        newRows.push({
          cameraId: cfg.cameraId,
          analysisNumber: displayName,
          type: "zone",
          name: zone.analysisConfig?.name ?? "",
          key: zone.key,
          result: null,
          reasons: [],
        });
      }
    }

    newRows.sort((a, b) => compareCamId(a.analysisNumber, b.analysisNumber));
    setRows(newRows);
    setSortOrder("asc");
  }, [open, localConfigs, cameras, getDisplayName]);

  // B7: 닫힐 때 중첩 Radix Tooltip(Fail 배지)과 얽혀 body pointer-events:none 가 잔존하는 케이스 방어
  useRestoreBodyPointerEvents(open);

  const handleToggleSort = () => {
    const next: SortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    setRows((prev) =>
      [...prev].sort((a, b) => {
        const cmp = compareCamId(a.analysisNumber, b.analysisNumber);
        return next === "asc" ? cmp : -cmp;
      }),
    );
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const { fetchEventSourceKeyWhitelist } = await import(
        "~/features/analysis-config/api/fetch-event-source-key-whitelist"
      );
      const whitelist = await fetchEventSourceKeyWhitelist(clientUid!);
      const whitelistSet = new Set(whitelist.map((w) => w.toLowerCase()));

      setRows((prev) =>
        prev.map((row) => {
          const suffix = extractKeySuffix(row.key);
          const { ok, reasons } = validateKey(suffix, whitelistSet);
          return { ...row, result: ok ? "pass" : "fail", reasons };
        }),
      );
    } catch {
      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          result: "fail",
          reasons: [{ key: "validation.reason.runtimeError" }],
        })),
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <StyledDialogContent
        withOverlay
        onCloseAutoFocus={(e) => {
          // Radix 가 close cleanup(body inert/aria-hidden/scroll-lock 정리) 을 마친 후 호출되는 콜백.
          // pendingNav 가 있으면 focus 복원 대신 navigate.
          if (pendingNavRef.current) {
            e.preventDefault();
            const target = pendingNavRef.current;
            pendingNavRef.current = null;
            navigate(target);
          }
        }}
      >
        <DialogBody ref={dialogBodyRef}>
          <DialogHeader>
            <HeaderTitle>{t("validation.title")}</HeaderTitle>
            <CloseButton onClick={onClose}>
              <Icon icon="ic_close" width={16} />
            </CloseButton>
          </DialogHeader>

          <WhitelistLink
            onClick={() => {
              // navigate 를 직접 호출하지 않고 pending 표시 + dialog 닫기만.
              // 실제 navigate 는 위의 onCloseAutoFocus 에서 cleanup 완료 후 실행됨.
              pendingNavRef.current = `/branch/${branchUid}/resources?tab=whitelist`;
              onClose();
            }}
          >
            <WhitelistLabel>{t("validation.whitelistSettings")}</WhitelistLabel>
            <Icon icon="ic_external_link" width={14} />
          </WhitelistLink>

          <TableContainer>
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <ThNumber>
                      <SortButton onClick={handleToggleSort}>
                        <span>{t("validation.col.analysisNumber")}</span>
                        <SortIcon $order={sortOrder}>
                          <Icon icon="ic_dropdown_down_large" width={16} />
                        </SortIcon>
                      </SortButton>
                    </ThNumber>
                    <Th>{t("validation.col.name")}</Th>
                    <Th>{t("validation.col.key")}</Th>
                    <ThResult>{t("validation.col.result")}</ThResult>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.key}-${i}`}>
                      <TdNumber>{row.analysisNumber}</TdNumber>
                      <Td>{row.name}</Td>
                      <Td>{row.key}</Td>
                      <TdResult>
                        {row.result === "pass" && (
                          <Label variant="soft" color="success" size="xSmall">Pass</Label>
                        )}
                        {row.result === "fail" && (
                          <Tooltip
                            trigger={<Label variant="soft" color="error" size="xSmall">Fail</Label>}
                            side="left"
                            sideOffset={4}
                            container={dialogBodyRef.current}
                          >
                            <TooltipList>
                              {row.reasons.map((r, ri) => (
                                <li key={ri}>{t(r.key, r.values)}</li>
                              ))}
                            </TooltipList>
                          </Tooltip>
                        )}
                      </TdResult>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <Td colSpan={4} style={{ textAlign: "center" }}>
                        <EmptyText>{t("validation.empty")}</EmptyText>
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          </TableContainer>

          <DialogFooter>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleValidate}
              disabled={isValidating || rows.length === 0}
            >
              {t("validation.runValidation")}
            </Button>
          </DialogFooter>
        </DialogBody>
      </StyledDialogContent>
    </Dialog.Root>
  );
}

// --- Styles ---

const StyledDialogContent = styled(Dialog.Content)`
  z-index: 100;
`;

const DialogBody = styled.div`
  width: 592px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  flex: 1;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;


const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${token("colors.text.primary")};
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const WhitelistLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: #fafbff;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  color: #696f8c;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const WhitelistLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.5;
  color: #696f8c;
`;

const TableContainer = styled.div`
  max-height: 372px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: Pretendard Variable, Pretendard, sans-serif;
`;

const ThNumber = styled.th`
  width: 92px;
  text-align: left;
  padding: 0;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  border-right: 1px solid #d8dae5;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 2px 8px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.57;
  color: #11111f;

  &:hover {
    opacity: 0.7;
  }
`;

const SortIcon = styled.span<{ $order: SortOrder }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #242946;
  transition: transform 0.15s ease;
  transform: ${({ $order }) => ($order === "asc" ? "rotate(0deg)" : "rotate(180deg)")};
  line-height: 0;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.57;
  color: ${token("colors.text.secondary")};
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

const ThResult = styled.th`
  width: 56px;
  text-align: left;
  padding: 6px 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.57;
  color: ${token("colors.text.secondary")};
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`;

const TdNumber = styled.td`
  width: 92px;
  text-align: left;
  padding: 7px 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.57;
  color: #11111f;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
  border-right: 1px solid #d8dae5;
`;

const Td = styled.td`
  text-align: left;
  padding: 7px 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.57;
  color: #11111f;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
`;

const TdResult = styled.td`
  width: 56px;
  text-align: left;
  padding: 7px 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.57;
  color: #11111f;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
`;

const EmptyText = styled.span`
  color: ${token("colors.text.secondary")};
  font-size: 14px;
  padding: 24px 0;
  display: block;
`;

const TooltipList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.4;

  li::before {
    content: "·";
    margin-right: 4px;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

